/**
 * 1.6.6 — two crews on one day (D-1.6.6-1): lanes, tags, and "not yours".
 *
 * The day's crew split lives in the app's own file, `_app/day-crews/<yyyyMMdd>.json` — no list, no column. An absent
 * file is one crew: everyone A, no lanes, the day exactly as it was before 1.6.6.
 *
 * SharePoint keeps day, period and position; the lane is how the app lays the positions out. With two crews every
 * position of the day sits in one lane at one place (`spots`: position → crew and place in the lane), each lane has
 * its own clock from the day's start, and a visit is in the lane its position is in. So a booking, a move and "Put
 * the day in order" — which all change positions through the scheduler — need nothing new: the visit lands where
 * its position sits. Moving a visit to the other crew changes the file only: its position trades places with the
 * other lane's first free position (so nobody else's time changes), or is added at the end of that lane when it has
 * no free position.
 *
 * No dependencies beyond the day plan, so the app and the API share it.
 */
import type { CrewAssignment } from './admin';
import type { LaneSpot, LaneSpots } from './dayplan';

export type Lane = CrewAssignment;
export const LANES: readonly Lane[] = ['A', 'B'];
export const otherLane = (l: Lane): Lane => (l === 'A' ? 'B' : 'A');
export type { LaneSpot, LaneSpots };

/** The highest position the scheduler keeps (DailySequenceNumber 1–12). */
export const LANE_MAX_POSITION = 12;
/** "One or two people" per crew in the app; the others on the team are guests without a sign-in. */
export const MAX_CREW_MEMBERS = 2;

/** With two crews the "bring in another team member" warning comes at the 7th visit of the day — never a block. */
export const TWO_CREW_WARN_AT = 7;

export const DAY_CREWS_FOLDER = '_app/day-crews';
export const DAY_CREWS_EVENT = 'DAY_CREWS_CHANGED';

/** The file, as stored. `spots` and `off` are the app's own additions to the prompt's shape (D-1.6.6-1). */
export interface DayCrewsFile {
  crews: 1 | 2;
  /** The day's own members (sign-in addresses); null = the standing crews from Admin → Users. */
  members: Record<Lane, string[]> | null;
  /** "Not today": crew accounts placed on neither crew for this day. */
  off?: string[];
  /** Which crew each visit was with when the file was last saved (the record; the layout is `spots`). */
  visits: Record<string, Lane>;
  /** The layout: each position's crew and its place in that crew's lane. */
  spots?: Record<string, LaneSpot> | null;
  updatedBy: string;
  updatedAt: string;
}

/** The split a day starts with: positions in order, the first half to A (1, 2, 3 → A; 4, 5, 6 → B). */
export function defaultSpots(baseCapacity: number): LaneSpots {
  const half = Math.max(1, Math.ceil(Math.max(2, baseCapacity) / 2));
  const out: LaneSpots = {};
  for (let p = 1; p <= LANE_MAX_POSITION; p++) out[p] = p <= half ? { crew: 'A', n: p } : { crew: 'B', n: p - half };
  return out;
}

/** The file's layout as numbers, completed with the default split for a position it does not name. */
export function spotsOf(file: Pick<DayCrewsFile, 'crews' | 'spots'> | null | undefined, baseCapacity: number): LaneSpots | null {
  if (!file || file.crews !== 2) return null;
  const out = defaultSpots(baseCapacity);
  for (const [k, v] of Object.entries(file.spots ?? {})) {
    const p = Number(k);
    if (Number.isInteger(p) && p >= 1 && p <= LANE_MAX_POSITION && v && (v.crew === 'A' || v.crew === 'B') && Number.isInteger(v.n) && v.n >= 1) out[p] = { crew: v.crew, n: v.n };
  }
  return out;
}

export const laneOf = (spots: LaneSpots | null | undefined, position: number | null | undefined): Lane | null => (spots && position ? (spots[position]?.crew ?? null) : null);

/** The positions of one lane, in the lane's own order. */
export function lanePositions(spots: LaneSpots, lane: Lane, upTo: number = LANE_MAX_POSITION): number[] {
  return Object.keys(spots)
    .map(Number)
    .filter((p) => p <= upTo && spots[p]!.crew === lane)
    .sort((a, b) => spots[a]!.n - spots[b]!.n || a - b);
}

export class LaneMoveRefused extends Error {}

/**
 * Moves the visit at `position` to the other crew. `taken`: the positions that hold a visit on the day. The visit
 * takes the place of that lane's first free position (the two trade places, so nobody else's time changes); when
 * the lane has no free position it goes to the end of the lane and leaves its old time empty.
 */
export function moveToLane(spots: LaneSpots, position: number, to: Lane, taken: readonly number[]): LaneSpots {
  const from = spots[position];
  if (!from) throw new LaneMoveRefused('That visit holds no time on the day.');
  if (from.crew === to) return spots;
  const held = new Set(taken);
  const free = lanePositions(spots, to).find((p) => !held.has(p));
  const out: LaneSpots = { ...spots };
  if (free !== undefined) {
    out[position] = spots[free]!;
    out[free] = from;
    return out;
  }
  const last = Math.max(0, ...lanePositions(spots, to).map((p) => spots[p]!.n));
  out[position] = { crew: to, n: last + 1 };
  return out;
}

/** Which crew each visit is with, from where its position sits. */
export function visitLanes(spots: LaneSpots | null, visits: readonly { appointmentId: string; position: number | null }[]): Record<string, Lane> {
  const out: Record<string, Lane> = {};
  if (!spots) return out;
  for (const v of visits) {
    const l = laneOf(spots, v.position);
    if (l) out[v.appointmentId] = l;
  }
  return out;
}

/* ---------------- who is on which crew ---------------- */

export interface CrewPerson {
  email: string;
  /** The standing crew from Admin → Users (null: never set). */
  standing: Lane | null;
}

const key = (email: string) => email.trim().toLowerCase();

/**
 * A person's crew on a two-crew day: the day's own list first, their standing crew second; null when they are on
 * neither ("not today", or a new sign-in nobody has placed).
 */
export function memberLane(file: Pick<DayCrewsFile, 'members' | 'off'> | null | undefined, person: CrewPerson): Lane | null {
  const k = key(person.email);
  if (file?.members) {
    if (file.members.A.some((e) => key(e) === k)) return 'A';
    if (file.members.B.some((e) => key(e) === k)) return 'B';
  }
  if ((file?.off ?? []).some((e) => key(e) === k)) return null;
  return person.standing;
}

/** The day's members as lists (the day's own choices over the standing crews). */
export function dayMembers(file: Pick<DayCrewsFile, 'members' | 'off'> | null | undefined, people: readonly CrewPerson[]): Record<Lane, string[]> & { none: string[] } {
  const out = { A: [] as string[], B: [] as string[], none: [] as string[] };
  const seen = new Set<string>();
  const put = (email: string, lane: Lane | null) => {
    const k = key(email);
    if (seen.has(k)) return;
    seen.add(k);
    (lane ? out[lane] : out.none).push(k);
  };
  for (const p of people) put(p.email, memberLane(file, p));
  // a person the day's list names who is not (or no longer) among the crew accounts stays where the office put them
  for (const l of LANES) for (const e of file?.members?.[l] ?? []) put(e, l);
  return out;
}

/* ---------------- the API ---------------- */

export interface DayCrewsPerson extends CrewPerson {
  name: string;
  /** Today's crew (null: on neither). */
  lane: Lane | null;
}

export interface DayCrewsVisit {
  appointmentId: string;
  unit: string;
  name: string;
  position: number;
  lane: Lane;
  /** Its planned start in its lane (minutes after midnight). */
  startMinutes: number | null;
  /** Its planned length, and whether it is a tenant's visit (only a tenant is emailed a new time). */
  minutes: number;
  tenant: boolean;
  /** Today's visit the crew has already been to, a visit under way or over: it stays with its crew. */
  fixed: boolean;
}

/** GET /api/housekeeping/day-crews/{dayKey} */
export interface DayCrewsResponse {
  dayKey: string;
  crews: 1 | 2;
  /** With two crews: the layout. With one: the split the day would start with — shown for review before Save. */
  spots: LaneSpots;
  visits: DayCrewsVisit[];
  people: DayCrewsPerson[];
  /** What lays the day's clock times out (shared/dayplan.ts planDay), so the page can show a change before Save. */
  settings: Record<string, string>;
  /** The caller may change the number of crews and the members (the administrator, a co-administrator). */
  canManage: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** PUT /api/housekeeping/day-crews/{dayKey} */
export interface DayCrewsRequest {
  crews?: 1 | 2;
  /** Visits to put with a crew (only those that change need naming). */
  visits?: Record<string, Lane>;
  /** People's crew for the day: 'A', 'B', or null for "not today". */
  members?: Record<string, Lane | null>;
  /** "Email the tenant their new time" — off unless the person switched it on. */
  emailMoved?: boolean;
}

export interface DayCrewsMoved {
  appointmentId: string;
  unit: string;
  /** The crew before and after (null: the day had, or has, one crew). */
  from: Lane | null;
  to: Lane | null;
  /** Planned start before and after (minutes after midnight). */
  was: number | null;
  now: number | null;
}

export interface DayCrewsSaved extends DayCrewsResponse {
  moved: DayCrewsMoved[];
  emailsQueued: number;
}

/** What Today and Planning carry for a two-crew day. */
export interface DayCrewsLite {
  spots: LaneSpots;
  /** First names of each crew's people in the app (empty: "nobody signed in"). */
  members: Record<Lane, string[]>;
  /** Crew view: the caller's own crew today (null: not placed — "Ask the office which crew you are on today"). */
  mine?: Lane | null;
}

/** The words the server refuses the other crew's visit with (the phone shows the same). */
export const notYoursWords = (lane: Lane): string => `Crew ${lane} — not yours.`;
export const ASK_OFFICE_WORDS = 'Ask the office which crew you are on today.';
